import { useEffect, useState } from 'react';
import { Upload, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrderQuotaImportResult } from '../graphql/orderQuotaGraphql';
import { MAX_IMPORT_ROWS, TEMPLATE_HEADER_MISMATCH, formatLineNumbers } from '../utils/orderQuotaExcel';

interface SheetImportOptions<Row> {
    open: boolean;
    /** 解析文件的第一个工作表；前三列表头与模板不一致时抛出 [TEMPLATE_HEADER_MISMATCH] */
    parse: (data: ArrayBuffer) => Row[];
    /** dryRun 为 true 时只校验并返回预览 */
    onImport: (rows: Row[], dryRun: boolean) => Promise<OrderQuotaImportResult>;
    /** 已写入后通知调用方刷新 */
    onApplied?: () => Promise<void>;
    /** 全部写入、没有错误行时关闭弹窗 */
    onClose: () => void;
}

/** 导入后跳过错误行的提示：确认导入后的提示与预览区共用 */
export const useSkippedText = () => {
    const { t } = useTranslation();
    return (result: OrderQuotaImportResult): string =>
        t('central.orderQuota.import.appliedWithSkipped', {
            count: result.validCount,
            skipped: result.errors.length,
            lines: formatLineNumbers(result.errors.map((error) => error.lineNo)),
        }) as string;
};

/**
 * 导入弹窗的共用流程：选文件 → 解析 → 预览（dryRun）→ 确认导入；前端只负责解析，校验以后端预览结果为准。
 * 重新选文件时先清掉上一个文件的数据与预览，新文件校验不通过时不能误导入旧文件；
 * 有错误行时确认导入会跳过这些行，导入后保留错误明细，便于修正后重新导入。
 * 后端整体拒绝（如容量校验不通过）的原因可能有多行，放在 rejection 中由弹窗展示，不弹 toast（toast 不换行且很快消失）。
 */
export const useSheetImport = <Row>({ open, parse, onImport, onApplied, onClose }: SheetImportOptions<Row>) => {
    const { t } = useTranslation();
    const skippedText = useSkippedText();
    const [rows, setRows] = useState<Row[] | null>(null);
    const [preview, setPreview] = useState<OrderQuotaImportResult | null>(null);
    const [rejection, setRejection] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setRows(null);
            setPreview(null);
            setRejection(null);
        }
    }, [open]);

    const handleFile = async (file: File) => {
        setRows(null);
        setPreview(null);
        setRejection(null);
        try {
            const parsed = parse(await file.arrayBuffer());
            if (parsed.length === 0) {
                message.error(t('central.orderQuota.import.empty'));
            } else if (parsed.length > MAX_IMPORT_ROWS) {
                message.error(t('central.orderQuota.import.tooMany', { max: MAX_IMPORT_ROWS }));
            } else {
                setRows(parsed);
                setLoading(true);
                try {
                    setPreview(await onImport(parsed, true));
                } catch (e) {
                    setPreview(null);
                    setRejection((e as Error).message);
                } finally {
                    setLoading(false);
                }
            }
        } catch (e) {
            const mismatch = (e as Error).message === TEMPLATE_HEADER_MISMATCH;
            message.error(t(mismatch ? 'central.orderQuota.import.headerMismatch' : 'central.orderQuota.import.parseFailed'));
        }
        return Upload.LIST_IGNORE;
    };

    const handleConfirm = async () => {
        if (!rows) return;
        setLoading(true);
        setRejection(null);
        try {
            const result = await onImport(rows, false);
            if (result.applied) await onApplied?.();
            if (result.applied && result.errors.length === 0) {
                message.success(t('central.orderQuota.import.applied', { count: result.validCount }));
                onClose();
                return;
            }
            if (result.applied) message.warning(skippedText(result));
            setPreview(result);
        } catch (e) {
            setRejection((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    /* 没有预览、没有有效行或已导入时不能确认 */
    const blocked = !preview || preview.validCount === 0 || preview.applied;

    return { preview, rejection, loading, blocked, handleFile, handleConfirm };
};
